/* External */
import {
  memo,
  createContext,
  useEffect,
  useContext,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { isDefaultPlatform } from '../../utils/platform';

const ForegroundAppContext = createContext(true);

// You can change false to true to test visibility change behavior in browser
const USE_FOREGROUND_IN_DEFAULT_PLATFORM = false;
const SHOULD_ADD_VISIBILITYCHANGE_LISTENER = !isDefaultPlatform()
  || USE_FOREGROUND_IN_DEFAULT_PLATFORM;

function ForegroundAppProvider({ children = undefined }) {
  const [isForegroundApp, setIsForegroundApp] = useState(
    (SHOULD_ADD_VISIBILITYCHANGE_LISTENER && !document.hidden)
      || true,
  );

  const listener = useCallback(() => {
    setIsForegroundApp(!document.hidden);
  }, []);

  useOnVisibilityChange(listener);

  return (
    <ForegroundAppContext.Provider value={isForegroundApp}>
      {children}
    </ForegroundAppContext.Provider>
  );
}

ForegroundAppProvider.propTypes = {
  children: PropTypes.node,
};

export default memo(ForegroundAppProvider);

export function useIsForegroundApp() {
  return useContext(ForegroundAppContext);
}

export function useOnVisibilityChange(listener, force = false) {
  useEffect(() => {
    if (SHOULD_ADD_VISIBILITYCHANGE_LISTENER || force) {
      document.addEventListener('visibilitychange', listener, true);
    }
    return () => {
      document.removeEventListener('visibilitychange', listener, true);
    };
  }, [force, listener]);
}

export function useOnBackground(onBackground, force = false) {
  const listener = useCallback((e) => {
    if (document.hidden) onBackground(e);
  }, [onBackground]);

  useOnVisibilityChange(listener, force);
}

export function useOnForeground(onForeground, force = false) {
  const listener = useCallback((e) => {
    if (!document.hidden) onForeground(e);
  }, [onForeground]);

  useOnVisibilityChange(listener, force);
}
